<template>
  <el-select
    v-model="value"
    multiple
    filterable
    remote
    reserve-keyword
    :disabled="!countryIso.length"
    :placeholder="
      countryIso.length
        ? `Start typing ${target}`
        : 'Select at least one country'
    "
    :remote-method="lookup"
    :loading="loading"
    @update:modelValue="updated"
  >
    <el-option
      v-for="item in options"
      :key="item.key"
      :label="item.label"
      :value="item.key"
    >
      {{ item.label }}
    </el-option>
  </el-select>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, watch } from 'vue'
import { useRegionsStore } from '@/store/modules/regions'
import { IRegion, IRegionsPayload, Option } from '@/api/regions'

export default defineComponent({
  name: 'RegionSelector',
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    target: {
      type: String,
      required: true
    },
    countryIso: {
      type: Array,
      required: true
    }
  },
  setup(props, context) {
    const value = ref(props.modelValue)

    const regionsStore = useRegionsStore()
    const options = reactive<Array<Option>>([])
    const loading = ref(false)

    function updated(newValue: Array<IRegion>) {
      context.emit('update:modelValue', newValue)
    }

    const loadInitial = async () => {
      if (props.modelValue.length < 1) {
        return
      }
      const payload = {
        filter: {
          geoip2_id: props.modelValue,
          target: props.target
        },
        limit: props.modelValue.length,
        start: 0
      } as IRegionsPayload
      const response = await regionsStore.SearchItems(payload)
      assignOptions(response)
    }

    async function lookup(query: string) {
      if (query.length >= 2) {
        loading.value = true
        const payload = {
          filter: {
            name__or__geoip2_id: query,
            country_iso: props.countryIso,
            target: props.target
          },
          limit: 50,
          start: 0
        } as IRegionsPayload
        const response = await regionsStore.SearchItems(payload)
        assignOptions(response)
        loading.value = false
      } else {
        options.splice(0, options.length, ...[])
      }
    }

    watch(
      (_: any) => props.target,
      (_) => options.splice(0, options.length, ...[])
    )

    function assignOptions(items: Array<IRegion>) {
      const newOptions = items.map((item: IRegion) => {
        return {
          key: Number(item.attributes.geoip2_id),
          label: item.attributes.name
        } as Option
      })
      options.splice(0, options.length, ...newOptions)
    }
    loadInitial()

    return { options, lookup, loading, updated, value }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
</style>
